import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Segmented,
  Select,
  Skeleton,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faCheck,
  faCircleExclamation,
  faGear,
  faMobileScreen,
  faPlus,
  faRotate,
  faShieldCheck,
  faStore,
} from '@fortawesome/pro-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppUpdatePlatformPolicy,
  getAppUpdatePolicy,
  updateAppUpdatePolicy,
} from '../../services/connectConfig';
import {
  DiscoveredRelease,
  getDiscoveredReleases,
} from '../../services/releases';
import {
  DEV_IOS_MONOTONIC_BUILD_START,
  cutoffReleases,
  preferredReleaseNote,
  supportsForcedUpdates,
} from './releasePolicy';
import AppVersionsSection, { useAppVersions } from './AppVersionsSection';
import CompatibilityUsageSection from './CompatibilityUsageSection';
import { activeDevicesBelow } from './appVersions';
import './ReleaseConsole.css';

type ConnectEnvironment = 'dev' | 'prod';
type Platform = 'android' | 'ios';
type UpdateBehavior = 'allow' | 'require-latest' | 'custom';

interface ReleaseValues {
  version: string;
  build: number;
  behavior: UpdateBehavior;
  customCutoff?: number;
  message: string;
}

interface DefaultValues {
  updateUrl: string;
}

const DEFAULT_MESSAGE = 'A newer production version of Ourora is required to continue.';

function policyThreshold(policy?: AppUpdatePlatformPolicy): number {
  return policy?.forceUpdateBelowBuild || 0;
}

function blockedCopy(threshold: number, platformName: string): string {
  return threshold > 0
    ? `${platformName} builds ${threshold - 1} and earlier must update.`
    : `No ${platformName} users are being forced to update.`;
}

function releaseDate(value?: string): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

const ReleaseConsole: React.FC = () => {
  const queryClient = useQueryClient();
  const [releaseForm] = Form.useForm<ReleaseValues>();
  const [defaultsForm] = Form.useForm<DefaultValues>();
  const [environment, setEnvironment] = React.useState<ConnectEnvironment>('dev');
  const [platform, setPlatform] = React.useState<Platform>('android');
  const [assistantOpen, setAssistantOpen] = React.useState(false);
  const [assistantDirty, setAssistantDirty] = React.useState(false);
  const [defaultsOpen, setDefaultsOpen] = React.useState(false);
  const [testBuild, setTestBuild] = React.useState<number | null>(null);
  const [productionConfirmed, setProductionConfirmed] = React.useState(false);
  const [selectedRelease, setSelectedRelease] = React.useState<DiscoveredRelease | null>(null);

  const query = useQuery({
    queryKey: ['appUpdatePolicy', environment],
    queryFn: () => getAppUpdatePolicy(environment),
  });

  const discoveryQuery = useQuery({
    queryKey: ['releaseDiscovery', environment, platform],
    queryFn: () => getDiscoveredReleases(environment, platform),
    retry: false,
  });

  const versionsQuery = useAppVersions(environment);

  const policy = query.data?.[platform];
  const threshold = policyThreshold(policy);
  const platformName = platform === 'android' ? 'Android' : 'iOS';
  const storeName = platform === 'android'
    ? 'Play Store'
    : environment === 'dev' ? 'TestFlight' : 'App Store';
  // Manual fields are not mounted for a detected release. Preserve their
  // programmatic values so the outcome and mutation use the selected build.
  const watchOptions = { form: releaseForm, preserve: true } as const;
  const behavior = Form.useWatch('behavior', watchOptions) || 'allow';
  const releaseBuild = Form.useWatch('build', watchOptions) || 0;
  const customCutoff = Form.useWatch('customCutoff', watchOptions) || 0;
  const releaseVersion = Form.useWatch('version', watchOptions) || '';
  const storeReleaseNote = preferredReleaseNote(selectedRelease);
  const devIosLegacyBuild = releaseBuild > 0
    && !supportsForcedUpdates(environment, platform, releaseBuild);
  const nextThreshold = behavior === 'allow'
    ? 0
    : behavior === 'require-latest'
      ? releaseBuild
      : customCutoff;
  const discoveredReleases = discoveryQuery.data?.releases || [];
  const cutoffOptions = cutoffReleases(discoveredReleases, releaseBuild);
  const visibleReleases = discoveredReleases.slice(0, 6);
  const latestSelectableRelease = discoveredReleases.find((release) => release.canApplyPolicy);
  if (latestSelectableRelease && !visibleReleases.some((release) => release.id === latestSelectableRelease.id)) {
    visibleReleases.push(latestSelectableRelease);
  }

  const policyMutation = useMutation({
    mutationFn: ({
      targetEnv,
      targetPlatform,
      nextPolicy,
    }: {
      targetEnv: ConnectEnvironment;
      targetPlatform: Platform;
      nextPolicy: AppUpdatePlatformPolicy;
    }) => updateAppUpdatePolicy({
      env: targetEnv,
      payload: { [targetPlatform]: nextPolicy },
    }),
    onSuccess: (_data, variables) => {
      message.success(`${variables.targetPlatform === 'android' ? 'Android' : 'iOS'} policy updated`);
      setAssistantOpen(false);
      setDefaultsOpen(false);
      setAssistantDirty(false);
      setProductionConfirmed(false);
      setSelectedRelease(null);
      queryClient.invalidateQueries({ queryKey: ['appUpdatePolicy', variables.targetEnv] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Could not update the release policy');
    },
  });

  const switchContext = (change: () => void) => {
    setTestBuild(null);
    change();
  };

  const openAssistant = (release?: DiscoveredRelease) => {
    if (!policy) return;
    releaseForm.setFieldsValue({
      version: release?.version || policy.latestProductionVersion,
      build: release?.build || policy.latestProductionBuild || undefined,
      behavior: !supportsForcedUpdates(
        environment,
        platform,
        release?.build || policy.latestProductionBuild || 0,
      )
        ? 'allow'
        : threshold > 0 ? 'custom' : 'allow',
      customCutoff: threshold || undefined,
      message: preferredReleaseNote(release) || policy.message || DEFAULT_MESSAGE,
    });
    setAssistantDirty(false);
    setSelectedRelease(release || null);
    setProductionConfirmed(environment !== 'prod' || Boolean(release?.canApplyPolicy));
    setAssistantOpen(true);
  };

  const refreshPage = async () => {
    const tasks: Array<Promise<unknown>> = [query.refetch(), versionsQuery.refetch()];
    tasks.push(
      queryClient.invalidateQueries({ queryKey: ['compatibilityUsage', environment] }),
    );
    tasks.push(
      getDiscoveredReleases(environment, platform, true)
        .then((data) => queryClient.setQueryData(
          ['releaseDiscovery', environment, platform],
          data,
        )),
    );
    await Promise.allSettled(tasks);
  };

  const closeAssistant = () => {
    if (!assistantDirty) {
      setAssistantOpen(false);
      return;
    }
    Modal.confirm({
      title: 'Close release setup?',
      content: 'The values in this setup have not been applied.',
      okText: 'Close setup',
      okButtonProps: { danger: true },
      onOk: () => setAssistantOpen(false),
    });
  };

  const applyRelease = async () => {
    if (!policy) return;
    await releaseForm.validateFields();
    // Detected releases render a summary instead of the manual version/build
    // fields. Those values still live in the form store, but validateFields()
    // only returns values for fields that are currently mounted.
    const values = releaseForm.getFieldsValue(true) as ReleaseValues;
    const cutoff = values.behavior === 'allow'
      ? 0
      : values.behavior === 'require-latest'
        ? values.build
        : values.customCutoff || 0;
    policyMutation.mutate({
      targetEnv: environment,
      targetPlatform: platform,
      nextPolicy: {
        minSupportedBuild: cutoff,
        forceUpdateBelowBuild: cutoff,
        latestProductionBuild: values.build,
        latestProductionVersion: values.version.trim(),
        updateUrl: policy.updateUrl,
        message: values.message.trim(),
      },
    });
  };

  const openDefaults = () => {
    if (!policy) return;
    defaultsForm.setFieldsValue({
      updateUrl: policy.updateUrl,
    });
    setDefaultsOpen(true);
  };

  const saveDefaults = async () => {
    if (!policy) return;
    const values = await defaultsForm.validateFields();
    policyMutation.mutate({
      targetEnv: environment,
      targetPlatform: platform,
      nextPolicy: { ...policy, ...values },
    });
  };

  const testResultBlocked = testBuild !== null && threshold > 0 && testBuild < threshold;

  return (
    <main className={`release-console release-console--${environment}`}>
      <header className="release-console-header">
        <div>
          <h1>App releases</h1>
          <p>Decide when people need to update Ourora.</p>
        </div>
        <div className="release-console-context">
          <Segmented
            value={environment}
            onChange={(value) => switchContext(() => setEnvironment(value as ConnectEnvironment))}
            options={[
              { label: 'Development', value: 'dev' },
              { label: 'Production', value: 'prod' },
            ]}
          />
          <Segmented
            value={platform}
            onChange={(value) => switchContext(() => setPlatform(value as Platform))}
            options={[
              { label: <span><FontAwesomeIcon icon={faAndroid} /> Android</span>, value: 'android' },
              { label: <span><FontAwesomeIcon icon={faApple} /> iOS</span>, value: 'ios' },
            ]}
          />
          <Button
            type="text"
            aria-label="Refresh releases and policy"
            icon={<FontAwesomeIcon icon={faRotate} spin={query.isFetching || discoveryQuery.isFetching} />}
            onClick={refreshPage}
            disabled={query.isFetching || discoveryQuery.isFetching}
          />
        </div>
      </header>

      {environment === 'prod' && (
        <div className="release-production-notice">
          <span /> You are viewing the live production policy.
        </div>
      )}

      {query.isLoading ? (
        <div className="release-console-loading"><Skeleton active paragraph={{ rows: 2 }} /></div>
      ) : query.isError || !policy ? (
        <div className="release-policy-unavailable" role="status">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <div>
            <strong>{environment === 'prod' ? 'Production policy is not connected yet' : 'Policy is temporarily unavailable'}</strong>
            <span>{(query.error as any)?.response?.data?.error || 'Check the Connect deployment and try again.'}</span>
          </div>
          <Button type="text" onClick={() => query.refetch()}>Try again</Button>
        </div>
      ) : (
        <section className="release-status" aria-label="Current update policy">
          <div className={`release-status-mark ${threshold > 0 ? 'is-blocking' : 'is-open'}`}>
            <FontAwesomeIcon icon={threshold > 0 ? faCircleExclamation : faShieldCheck} />
          </div>
          <div className="release-status-copy">
            <span>CURRENT {environment.toUpperCase()} POLICY</span>
            <h2>{blockedCopy(threshold, platformName)}</h2>
            <p>
              {policy.latestProductionVersion
                ? `Recorded release: ${policy.latestProductionVersion}${policy.latestProductionBuild ? ` · build ${policy.latestProductionBuild}` : ''}`
                : 'No release has been recorded yet.'}
            </p>
          </div>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => openAssistant(latestSelectableRelease)}
          >
            Set policy
          </Button>
        </section>
      )}

      <section className="release-ledger" aria-labelledby="release-ledger-title">
        <div className="release-section-intro release-ledger-intro">
          <div>
            <span>DETECTED FROM THE STORE</span>
            <h3 id="release-ledger-title">
              {discoveryQuery.data?.channelLabel || `${platformName} releases`}
            </h3>
            <p>
              {discoveryQuery.data?.note
                || 'Choose a verified release. Version and build are filled in automatically.'}
            </p>
          </div>
          {discoveryQuery.data && (
            <small>Checked {releaseDate(discoveryQuery.data.syncedAt)}</small>
          )}
        </div>

        {discoveryQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : discoveryQuery.isError ? (
          <Alert
            type="warning"
            showIcon
            message="Store releases are temporarily unavailable"
            description={(discoveryQuery.error as any)?.response?.data?.error || 'You can still enter a release manually.'}
            action={policy ? <Button onClick={() => openAssistant()}>Enter manually</Button> : undefined}
          />
        ) : visibleReleases.length === 0 ? (
          <div className="release-ledger-empty">
            <p>{discoveryQuery.data?.note || 'No releases were found in this channel.'}</p>
            {policy && <Button onClick={() => openAssistant()}>Enter manually</Button>}
          </div>
        ) : (
          <div className="release-ledger-list">
            {visibleReleases.map((release) => (
              <div className={`release-ledger-row ${release.canApplyPolicy ? 'is-selectable' : ''}`} key={release.id}>
                <div className="release-ledger-version">
                  <strong>{release.version || 'Version unavailable'}</strong>
                  <span>Build {release.build}</span>
                </div>
                <div className="release-ledger-state">
                  <span className={release.canApplyPolicy ? 'is-live' : ''} />
                  <div>
                    <strong>{release.statusLabel}</strong>
                    {release.discoveredAt && <small>{releaseDate(release.discoveredAt)}</small>}
                  </div>
                </div>
                {release.canApplyPolicy ? (
                  <Button type="link" disabled={!policy} onClick={() => openAssistant(release)}>
                    Set policy
                  </Button>
                ) : (
                  <span className="release-ledger-wait">Not selectable</span>
                )}
              </div>
            ))}
          </div>
        )}
        {visibleReleases.length > 0 && policy && (
          <Button className="release-manual-link" type="text" onClick={() => openAssistant()}>
            Enter a release manually
          </Button>
        )}
      </section>

      <AppVersionsSection
        environment={environment}
        platform={platform}
        platformName={platformName}
        threshold={threshold}
      />

      <CompatibilityUsageSection environment={environment} />

      <section className="release-how" aria-labelledby="release-how-title">
            <div className="release-section-intro">
              <span>HOW THIS WORKS</span>
              <h3 id="release-how-title">The app decides using build numbers.</h3>
              <p>Version names are for people. Build numbers are what the policy compares.</p>
            </div>

            <div className="release-flow">
              <div className="release-flow-step">
                <div><FontAwesomeIcon icon={faMobileScreen} /></div>
                <span>1</span>
                <strong>App opens</strong>
                <p>Ourora reports the installed platform and build number.</p>
              </div>
              <FontAwesomeIcon className="release-flow-arrow" icon={faArrowRight} />
              <div className="release-flow-step">
                <div><FontAwesomeIcon icon={faShieldCheck} /></div>
                <span>2</span>
                <strong>Policy checks it</strong>
                <p>Connect compares that build with the cutoff you chose.</p>
              </div>
              <FontAwesomeIcon className="release-flow-arrow" icon={faArrowRight} />
              <div className="release-flow-step">
                <div><FontAwesomeIcon icon={faStore} /></div>
                <span>3</span>
                <strong>Continue or update</strong>
                <p>Allowed builds continue. Older builds open the store screen.</p>
              </div>
            </div>

            <div className="release-try-it">
              <div>
                <span>TRY IT</span>
                <strong>What happens to build</strong>
                <InputNumber
                  min={1}
                  precision={0}
                  value={testBuild}
                  onChange={setTestBuild}
                  placeholder="e.g. 10352"
                  aria-label="Build number to test"
                />
                <strong>?</strong>
              </div>
              {testBuild === null ? (
                <p>Enter a build number to see the answer.</p>
              ) : (
                <div className={testResultBlocked ? 'release-result is-blocked' : 'release-result is-allowed'}>
                  <FontAwesomeIcon icon={testResultBlocked ? faCircleExclamation : faCheck} />
                  <span>
                    <strong>{testResultBlocked ? 'Update required' : 'Allowed to continue'}</strong>
                    {threshold > 0 ? `Current cutoff: build ${threshold}` : 'There is no active cutoff.'}
                  </span>
                </div>
              )}
            </div>
      </section>

      {policy && (
        <footer className="release-defaults-row">
            <div>
              <FontAwesomeIcon icon={faGear} />
              <span>
                <strong>Release defaults</strong>
                Permanent {storeName} destination
              </span>
            </div>
            <Button type="text" onClick={openDefaults}>View defaults</Button>
        </footer>
      )}

      <Drawer
        className="release-assistant"
        size={480}
        open={assistantOpen}
        title={(
          <div className="release-assistant-title">
            <span>{environment === 'prod' ? 'PRODUCTION' : 'DEVELOPMENT'} · {platformName.toUpperCase()}</span>
            <strong>Release policy</strong>
          </div>
        )}
        onClose={closeAssistant}
        destroyOnClose
        footer={(
          <div className="release-assistant-footer">
            <Button type="text" onClick={closeAssistant}>Cancel</Button>
            <Button
              type="primary"
              danger={environment === 'prod'}
              disabled={(environment === 'prod' && !productionConfirmed)
                || (nextThreshold > 0 && !policy?.updateUrl)}
              loading={policyMutation.isPending}
              onClick={applyRelease}
            >
              Apply policy
            </Button>
          </div>
        )}
      >
        <Form
          form={releaseForm}
          layout="vertical"
          requiredMark={false}
          onValuesChange={() => setAssistantDirty(true)}
        >
          <div className="release-assistant-step">
            {selectedRelease ? (
              <section className="release-selected" aria-label="Selected release">
                <div className="release-selected-icon">
                  <FontAwesomeIcon icon={platform === 'ios' ? faApple : faAndroid} />
                </div>
                <div>
                  <span>SELECTED RELEASE</span>
                  <strong>{selectedRelease.version} <small>Build {selectedRelease.build}</small></strong>
                  <p>{selectedRelease.statusLabel}</p>
                </div>
                <FontAwesomeIcon className="release-selected-check" icon={faCheck} />
              </section>
            ) : (
              <section className="release-manual-entry">
                <span>MANUAL RELEASE</span>
                <h3>Enter the release details</h3>
                <p>Use this only when the release cannot be detected automatically.</p>
              <div className="release-assistant-grid">
                <Form.Item name="version" label="Version" rules={[{ required: true, message: 'Enter the release version' }]}>
                  <Input placeholder="e.g. 9.0.4" autoFocus />
                </Form.Item>
                <Form.Item name="build" label="Build" rules={[{ required: true, message: 'Enter the release build' }]}>
                  <InputNumber min={1} precision={0} placeholder="e.g. 10353" />
                </Form.Item>
              </div>
              <div className="release-assistant-note">
                <FontAwesomeIcon icon={faMobileScreen} />
                <span><strong>{platformName}</strong> has its own build counter. It does not need to match the other platform.</span>
              </div>
              </section>
            )}

            <section className="release-behavior">
              <span>UPDATE BEHAVIOR</span>
              <h3>What should older builds do?</h3>
              <p>Choose the result. You can change it again at any time.</p>
              <Form.Item name="behavior" rules={[{ required: true }]}> 
                <Radio.Group className="release-behavior-options">
                  <Radio value="allow">
                    <strong>Allow</strong>
                    <span>Record this release without interrupting anyone.</span>
                  </Radio>
                  <Radio value="require-latest" disabled={devIosLegacyBuild}>
                    <strong>Require latest</strong>
                    <span>{devIosLegacyBuild
                      ? `Available from Dev build ${DEV_IOS_MONOTONIC_BUILD_START}.`
                      : `Builds below ${releaseBuild || 'this release'} must update.`}</span>
                  </Radio>
                  <Radio value="custom" disabled={devIosLegacyBuild}>
                    <strong>Set a cutoff</strong>
                    <span>{devIosLegacyBuild
                      ? `Available from Dev build ${DEV_IOS_MONOTONIC_BUILD_START}.`
                      : 'Keep more than one recent build supported.'}</span>
                  </Radio>
                </Radio.Group>
              </Form.Item>
              {behavior === 'custom' && (
                <Form.Item
                  className="release-custom-cutoff"
                  name="customCutoff"
                  label="First build that remains allowed"
                  rules={[
                    { required: true, message: 'Choose the first build that remains allowed' },
                    {
                      validator: (_, value) => !value || !releaseBuild || value <= releaseBuild
                        ? Promise.resolve()
                        : Promise.reject(new Error(`Cutoff cannot be higher than build ${releaseBuild}`)),
                    },
                  ]}
                >
                  {cutoffOptions.length > 0 ? (
                    <Select
                      placeholder="Select a detected build"
                      options={cutoffOptions.map((release) => ({
                        value: release.build,
                        label: `${release.version || 'Unknown version'} · Build ${release.build}`,
                      }))}
                    />
                  ) : (
                    <InputNumber
                      min={1}
                      max={releaseBuild || undefined}
                      precision={0}
                      placeholder="Build number"
                    />
                  )}
                </Form.Item>
              )}
              <section className="release-message-editor">
                <span>UPDATE MESSAGE</span>
                <div className="release-message-heading">
                  <h3>What should people know?</h3>
                  {storeReleaseNote && (
                    <Button
                      type="link"
                      onClick={() => {
                        releaseForm.setFieldValue('message', storeReleaseNote);
                        setAssistantDirty(true);
                      }}
                    >
                      Restore store text
                    </Button>
                  )}
                </div>
                <p>Shown when an older build is blocked. This is independent from the store listing.</p>
                <Form.Item
                  name="message"
                  rules={[
                    { required: true, whitespace: true, message: 'Add the update message' },
                    { max: 500, message: 'Keep the message within 500 characters' },
                  ]}
                  extra={storeReleaseNote
                    ? `Prefilled from ${storeName}; edit it freely.`
                    : 'Using the currently saved message; edit it for this release if needed.'}
                >
                  <Input.TextArea rows={4} maxLength={500} showCount />
                </Form.Item>
              </section>
              <div className={`release-outcome ${nextThreshold > 0 ? 'is-blocking' : ''}`}>
                <FontAwesomeIcon icon={nextThreshold > 0 ? faCircleExclamation : faShieldCheck} />
                <span>
                  <small>OUTCOME</small>
                  <strong>{nextThreshold > 0
                    ? `Build ${nextThreshold - 1} and earlier must update`
                    : 'No one will be forced to update'}</strong>
                  <p>{releaseVersion} · build {releaseBuild} will be recorded as the latest release.</p>
                  {nextThreshold > 0 && versionsQuery.data && (
                    <p>
                      {activeDevicesBelow(versionsQuery.data.platforms[platform], nextThreshold)} active
                      {' '}{platformName} devices are on older builds and will be asked to update.
                    </p>
                  )}
                </span>
              </div>
              {nextThreshold > 0 && !policy?.updateUrl && (
                <Alert type="error" showIcon message={`Add the ${storeName} destination in Release defaults first.`} />
              )}
              {environment === 'prod' && !selectedRelease && (
                <Checkbox checked={productionConfirmed} onChange={(event) => setProductionConfirmed(event.target.checked)}>
                  I verified that {releaseVersion} is available in the {storeName}.
                </Checkbox>
              )}
            </section>
          </div>
        </Form>
      </Drawer>

      <Modal
        open={defaultsOpen}
        title={`${platformName} release defaults`}
        okText="Save defaults"
        confirmLoading={policyMutation.isPending}
        onOk={saveDefaults}
        onCancel={() => setDefaultsOpen(false)}
      >
        <p className="release-defaults-explanation">
          These values usually stay the same. They are reused every time an update is required.
        </p>
        <Form form={defaultsForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="updateUrl"
            label={`${storeName} destination`}
            extra="The Update App button opens this address."
            rules={[
              { type: 'url', message: 'Enter a valid URL' },
              {
                validator: (_, value) => !value || value.startsWith('https://')
                  ? Promise.resolve()
                  : Promise.reject(new Error('The destination must use HTTPS')),
              },
            ]}
          >
            <Input
              placeholder="https://…"
              suffix={policy?.updateUrl ? (
                <a href={policy.updateUrl} target="_blank" rel="noreferrer" aria-label={`Open ${storeName}`}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              ) : null}
            />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
};

export default ReleaseConsole;
